/**
 * A monotonic request sequencer: the guard against stale responses.
 *
 * A screen that stays mounted while its subject changes — switching project,
 * switching package — can receive the OLD subject's response after the new
 * one has already rendered. Without a guard the slower, earlier request wins
 * and one project's providers appear under another project's name.
 *
 * Usage (one sequencer per independently-refreshable stream):
 *
 *   const seq = useRef(createSequencer()).current;
 *   const ticket = seq.next();
 *   const data = await fetchThing();
 *   if (!seq.isCurrent(ticket)) return;   // a newer load superseded this one
 *   setData(data);
 *
 * Pure and dependency-free so it is directly testable.
 */
export const createSequencer = () => {
  let current = 0;
  return {
    /** Claim the next ticket; every earlier ticket becomes stale. */
    next() {
      current += 1;
      return current;
    },
    /** True only for the most recently claimed ticket. */
    isCurrent(ticket) {
      return ticket === current;
    },
    /** Invalidate everything in flight without starting a new load. */
    invalidate() {
      current += 1;
    },
  };
};

export default createSequencer;
